import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, 
  Ticket, 
  Trash2, 
  Edit2, 
  Percent, 
  Euro,
  Calendar,
  Users,
  Copy,
  Check
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../../utils/errorHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoCodesTab() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    is_active: true
  });

  const fetchPromoCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/promo-codes`);
      setPromoCodes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des codes promo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '',
      max_uses: '',
      valid_from: '',
      valid_until: '',
      is_active: true
    });
    setEditingCode(null);
  };

  const handleOpenModal = (promoCode = null) => {
    if (promoCode) {
      setEditingCode(promoCode);
      setFormData({
        code: promoCode.code,
        description: promoCode.description || '',
        discount_type: promoCode.discount_type,
        discount_value: promoCode.discount_value.toString(),
        min_order_amount: promoCode.min_order_amount?.toString() || '',
        max_uses: promoCode.max_uses?.toString() || '',
        valid_from: promoCode.valid_from ? promoCode.valid_from.slice(0, 16) : '',
        valid_until: promoCode.valid_until ? promoCode.valid_until.slice(0, 16) : '',
        is_active: promoCode.is_active
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.discount_value) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const payload = {
      code: formData.code.toUpperCase(),
      description: formData.description || null,
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : 0,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
      valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
      is_active: formData.is_active
    };

    try {
      if (editingCode) {
        await axios.put(`${API}/promo-codes/${editingCode.id}`, payload);
        toast.success('Code promo mis à jour');
      } else {
        await axios.post(`${API}/promo-codes`, payload);
        toast.success('Code promo créé');
      }
      
      setModalOpen(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Une erreur est survenue');
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    
    try {
      await axios.delete(`${API}/promo-codes/${id}`);
      toast.success('Code promo supprimé');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (promoCode) => {
    try {
      await axios.put(`${API}/promo-codes/${promoCode.id}`, 
        { is_active: !promoCode.is_active }
      );
      toast.success(promoCode.is_active ? 'Code désactivé' : 'Code activé');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copié !');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Codes Promo</h2>
          <p className="text-sm text-gray-500">Créez des réductions avec des codes</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau code
        </Button>
      </div>

      {promoCodes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-900 mb-2">Aucun code promo</h3>
          <p className="text-gray-500 mb-4">Créez votre premier code promo</p>
          <Button onClick={() => handleOpenModal()} variant="outline" className="rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Créer un code
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {promoCodes.map((promo) => (
            <div 
              key={promo.id}
              className={`bg-white rounded-2xl border p-5 transition-all ${
                !promo.is_active || isExpired(promo.valid_until) 
                  ? 'border-gray-200 opacity-60' 
                  : 'border-emerald-100'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      onClick={() => copyCode(promo.code)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg font-mono font-bold text-emerald-700 hover:bg-emerald-100 transition-colors"
                    >
                      {promo.code}
                      {copiedCode === promo.code ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4 opacity-50" />
                      )}
                    </button>
                    
                    <Badge className={`${
                      promo.discount_type === 'percentage' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {promo.discount_type === 'percentage' ? (
                        <><Percent className="w-3 h-3 mr-1" />{promo.discount_value}%</>
                      ) : (
                        <><Euro className="w-3 h-3 mr-1" />{promo.discount_value}€</>
                      )}
                    </Badge>
                    
                    {!promo.is_active && (
                      <Badge className="bg-gray-100 text-gray-600">Désactivé</Badge>
                    )}
                    
                    {isExpired(promo.valid_until) && (
                      <Badge className="bg-red-100 text-red-600">Expiré</Badge>
                    )}
                  </div>
                  
                  {promo.description && (
                    <p className="text-gray-600 text-sm mb-3">{promo.description}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    {promo.min_order_amount > 0 && (
                      <span>Min. {promo.min_order_amount}€</span>
                    )}
                    {promo.max_uses && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {promo.current_uses || 0}/{promo.max_uses} utilisations
                      </span>
                    )}
                    {promo.valid_until && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Jusqu'au {new Date(promo.valid_until).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={promo.is_active}
                    onCheckedChange={() => handleToggleActive(promo)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenModal(promo)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(promo.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCode ? 'Modifier le code promo' : 'Nouveau code promo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="PROMO20"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div>
                <Label>Type de réduction</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                    <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valeur *</Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === 'percentage' ? '20' : '5'}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Commande minimum (€)</Label>
                <Input
                  type="number"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                  placeholder="0"
                  className="mt-1.5"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Réduction de bienvenue"
                className="mt-1.5"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Utilisations max</Label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Illimité"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Valide jusqu'au</Label>
                <Input
                  type="datetime-local"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2 pt-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Code actif</Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
                {editingCode ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
